import { createContext, useCallback, useContext, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal, flushSync } from 'react-dom';
import { useAppStore } from '../store/useAppStore';
import type { MarketingSnapshot } from '../api/marketingViews';
import { MARKETING_VIEW_PAGES, isMarketingSnapshot, validViewDates, matchesViewShape, viewFingerprint, viewPage } from '../lib/marketingViews';
import { MarketingSavedViews } from './MarketingSavedViews';
import { AdDataFreshnessContext } from './AdDataFreshnessContext';

type Section<T> = { label: string; state: T; restore: (state: T) => void; query?: () => void; afterQuery?: () => void; loading?: boolean; autoQuery?: boolean; resolve?: (state: T) => T; fingerprint?: (state: T) => string; validate?: (state: unknown) => boolean; summary?: string; details?: { label: string; text: string }[] };
type RegisteredSection = Section<unknown>;
type Registry = { restoring: boolean; set: (key: string, section: RegisteredSection) => void; remove: (key: string) => void };
const Context = createContext<Registry | null>(null);
export function useMarketingViewRestoring() {
  const registry = useContext(Context);
  return useCallback(() => registry?.restoring ?? false, [registry]);
}
/** Register only filter state, never rows, credentials, pagination, or export snapshots. */
export function useMarketingViewSection<T>(key: string, section: Section<T>) {
  const registry = useContext(Context);
  useLayoutEffect(() => { registry?.set(key, section as RegisteredSection); });
  useLayoutEffect(() => () => registry?.remove(key), [key, registry]);
}

export function MarketingViewsProvider({ path, active, children }: { path: string; active: boolean; children: ReactNode }) {
  const page = viewPage(path);
  if (!MARKETING_VIEW_PAGES.has(page)) return <>{children}</>;
  return <ViewPage page={page} active={active}>{children}</ViewPage>;
}
function ViewPage({ page, active, children }: { page: string; active: boolean; children: ReactNode }) {
  const brandId = useAppStore(s => s.brandId);
  const siteId = useAppStore(s => s.siteId);
  const sections = useRef(new Map<string, RegisteredSection>());
  const signatures = useRef(new Map<string, string>());
  const [, refresh] = useState(0);
  const [applying, setApplying] = useState(false);
  const registry = useRef<Registry>({
    restoring: false,
    set(key, section) {
      sections.current.set(key, section);
      const signature = viewFingerprint(section.state) + ':' + Boolean(section.loading) + ':' + section.summary + ':' + viewFingerprint(section.details);
      if (signatures.current.get(key) !== signature) { signatures.current.set(key, signature); refresh(v => v + 1); }
    },
    remove(key) { sections.current.delete(key); signatures.current.delete(key); refresh(v => v + 1); },
  }).current;
  const snapshot: MarketingSnapshot = { version: 1, sections: Object.fromEntries([...sections.current].map(([key, section]) => [key, section.state])) };
  const loading = applying || [...sections.current.values()].some(section => section.loading);
  const fingerprint = (value: MarketingSnapshot) => viewFingerprint(Object.fromEntries(Object.entries(value.sections).map(([key, state]) => [key, sections.current.get(key)?.fingerprint?.(state) ?? sections.current.get(key)?.resolve?.(structuredClone(state)) ?? state])));
  const prepare = (value: MarketingSnapshot) => {
    if (!isMarketingSnapshot(value)) throw new Error('视图格式已失效，请重新保存');
    return [...sections.current].map(([key, section]) => {
      const state = value.sections[key];
      if (state === undefined || !(section.validate ? section.validate(state) : matchesViewShape(state, section.state))) throw new Error('视图筛选与当前页面不兼容，请重新保存');
      const resolved = section.resolve ? section.resolve(structuredClone(state)) : structuredClone(state);
      if (!validViewDates(resolved)) throw new Error('请检查日期范围，开始日期不能晚于结束日期');
      return { key, state: resolved, section };
    });
  };
  const apply = (value: MarketingSnapshot) => {
    if (loading) return;
    const restore = prepare(value);
    // Commit all drafts and top query versions before reactive sections issue their requests.
    registry.restoring = true;
    try {
      flushSync(() => {
        setApplying(true);
        restore.forEach(item => item.section.restore(item.state));
      });
      flushSync(() => restore.forEach(item => {
        const latest = sections.current.get(item.key);
        if (!latest?.autoQuery || viewFingerprint(item.section.state) === viewFingerprint(item.state)) latest?.query?.();
      }));
      registry.restoring = false;
      flushSync(() => restore.forEach(item => sections.current.get(item.key)?.afterQuery?.()));
    } finally { registry.restoring = false; setApplying(false); }
  };
  const host = document.getElementById('marketing-saved-views-slot');
  return <Context.Provider value={registry}>
    <AdDataFreshnessContext.Provider value={page.startsWith('ad-analysis/ad-dashboard/') ? { page, active, reportLoading: loading } : null}>
      {children}
    </AdDataFreshnessContext.Provider>
    {host && createPortal(<MarketingSavedViews key={brandId + ':' + siteId} scope={{ brand_id: brandId, site_id: siteId, page }} active={active} snapshot={snapshot} onApply={apply} validate={value => { prepare(value); }} loading={loading} ready={sections.current.size > 0} fingerprint={fingerprint} />, host)}
  </Context.Provider>;
}
